// @ts-strict-ignore
import { ArticleConnectionPropertyValue } from '@article/constants/article-connection-property-values';
import { BaseReplaceArticleManager } from '@project/components/article/manager/base-replace-article.manager';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArrayUtils } from '@shared/util/array-utils';
import { Dictionary } from 'typescript-collections';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { ConnectionAreaVO, MetaElementUsageVO, TextureAreaUsageVO, TextValueVO } from '@shared/vo/meta-element.vo';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { CompositeArticleUtils } from '@article/util/composite-article-utils';
import { StringUtils } from '@shared/util/string-utils';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { Connection } from '@3d-scene/model3d/connection';
import { ArticleConnectionPropertyDef } from '@article/constants/article-connection-property-def';
import { PropertyUtils } from '@article/util/property-utils';
import { Vector3 } from 'three';
import { LabelingUtils } from '@3d-scene/model3d/util/labeling-utils';
import { DefinedMetaElementInfoPropertyDef } from '@project/constants/defined-meta-element-info-property-def';
import { AlternativeUsageConnectionData } from '@project/components/article/model/alternative-usage-connection-data';
import { CodingArticleUtils } from '@article/util/coding-article.utils';
import { ImageTextureProcessor } from '@3d-scene/processor/image-texture.processor';
import { SceneManager } from '@3d-scene/manager/scene.manager';
import { UserModel } from '@user/model/user.model';
import { Injectable } from '@angular/core';
import { DirectPrintingHelper } from '@article/helper/direct-printing/direct-printing.helper';
import { ArticleAccessoryManager } from '@article/manager/article-accessory.manager';
import { ProjectHelper } from '@project/helper/project.helper';
import { ProjectModel } from '@project/model/project.model';
import { UndoRedoUtils } from '@shared/undo/undo-redo-utils';
import { UndoRedoHelper } from '@shared/undo/undo-redo.helper';
import { LabelingArticleRotationHelper } from '../../label/helper/labeling-article-rotation.helper';
import { ReplaceLabelingUtils } from '../../label/replace-labeling-helper/replace-labeling-utils';
import { SelectionModel } from '../model/selection.model';
import { RotateArticlePresenter } from '../presenter/rotate-article.presenter';
import { InsertArticleUtils } from '../util/insert-article-utils';
import { Log } from '@shared/debug/log';
import { Observable, Subject } from 'rxjs';
import { InsertedArticleData } from '../processor/i-article-location.processor';
import { InsertArticleService } from '../service/insert-article.service';
import { ProjectModelService } from '@project/service/project-model.service';
import { ProjectArticleService } from '@project/service/project-article.service';
import { SelectionModelService } from '../service/selection-model.service';
import { ProjectMetaElementService } from '@project/manager/project-meta-element.service';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';
import { DetachCodingFingerArticleService } from '../service/detach-coding-finger-article.service';
import { ArticleConnectionService } from '@3d-scene/model3d/article-connection.service';
import { CompositeArticleInfoUtils } from '@article/util/composite-article-info-utils';

@Injectable({ providedIn: 'root' })
export class AlternativeReplaceArticleManager extends BaseReplaceArticleManager {
  private static readonly ALTERNATIVE_REPLACE_ARTICLE_MESSAGE_TYPE: string = 'alternativeReplaceArticleOnMountingRail';
  private static readonly REPLACE_ACCESSORIES_BY_ALTERNATIVE_MESSAGE_TYPE: string = 'replaceAccessoriesByAlternative';


  private _processedArticleContainer: ArticleContainer;
  private _commonArticleContainers: ArticleContainer[] = [];
  private _articleContainersToDetach: ArticleContainer[] = [];

  private _replacedArticles$ = new Subject<boolean>();

  constructor(
    userModel: UserModel,
    selectionModel: SelectionModel,
    selectionModelService: SelectionModelService,
    projectModel: ProjectModel,
    projectModelService: ProjectModelService,
    projectArticleService: ProjectArticleService,
    projectMetaElementService: ProjectMetaElementService,
    projectHelper: ProjectHelper,
    undoRedoHelper: UndoRedoHelper,
    articleAccessoryManager: ArticleAccessoryManager,
    directPrintingHelper: DirectPrintingHelper,
    containerService: ContainerService,
    undoRedoUtils: UndoRedoUtils,
    labelingArticleRotationHelper: LabelingArticleRotationHelper,
    replaceLabelingUtils: ReplaceLabelingUtils,
    compositeArticleUtils: CompositeArticleUtils,
    compositeArticleInfoUtils: CompositeArticleInfoUtils,
    rotateArticlePresenter: RotateArticlePresenter,
    labelingUtils: LabelingUtils,
    articleContainerUtils: ArticleContainerUtils,
    insertArticleUtils: InsertArticleUtils,
    insertArticleService: InsertArticleService,
    detachArticleUsageService: DetachArticleUsageService,
    articleConnectionService: ArticleConnectionService,
    private detachCodingFingerArticleService: DetachCodingFingerArticleService,
    private imageTextureProcessor: ImageTextureProcessor,
    private sceneManager: SceneManager,
  ) {
    super(
      userModel,
      selectionModel,
      selectionModelService,
      projectModel,
      projectModelService,
      projectArticleService,
      projectMetaElementService,
      projectHelper,
      undoRedoHelper,
      articleAccessoryManager,
      directPrintingHelper,
      containerService,
      undoRedoUtils,
      insertArticleUtils,
      labelingArticleRotationHelper,
      replaceLabelingUtils,
      compositeArticleUtils,
      compositeArticleInfoUtils,
      rotateArticlePresenter,
      labelingUtils,
      articleContainerUtils,
      insertArticleService,
      detachArticleUsageService,
      articleConnectionService
    );
  }

  public get replacedArticles$(): Observable<boolean> {
    return this._replacedArticles$.asObservable()
  }

  protected async init(articleContainersOnRail: ArticleContainer[], childContainers: ArticleContainer[], article: ArticleVO,
    unremovedComplexPartArticles: ArticleContainer[]): Promise<void> {
    await super.init(articleContainersOnRail, childContainers, article, unremovedComplexPartArticles);

    this._articleContainersToDetach = articleContainersOnRail.concat(unremovedComplexPartArticles);
    this._commonArticleContainers = articleContainersOnRail;
  }

  protected async startProcessingAccessoryReplacement(_accessoryContainers: ArticleContainer[]): Promise<void> {
    await this.replaceNextArticle();
  }

  protected async startProcessingPreparedArticles(): Promise<void> {
    await this.replaceNextArticle();
  }

  protected async replaceNextArticle(): Promise<void> {
    if (ArrayUtils.isNotEmpty(this._commonArticleContainers)) {
      this._processedArticleContainer = this._commonArticleContainers[0];
      this._commonArticleContainers.splice(this._commonArticleContainers.indexOf(this._processedArticleContainer), 1);

      this.processedUsageId = this._processedArticleContainer.articleGUID;
      if (this._accessoryReplaceEnabled) {
        try {
          await this.replaceAccessoryArticle(AlternativeReplaceArticleManager.REPLACE_ACCESSORIES_BY_ALTERNATIVE_MESSAGE_TYPE);
          await this.replaceAccessoriesByAlternative();
        } catch (error) {
          Log.error('Could not replace accessory article: ', error);
          this.replaceAccessoriesByAlternative_failure()
        }

      } else {
        try {
          const data: InsertedArticleData = await this.replaceArticle(this.processedUsageId, AlternativeReplaceArticleManager.ALTERNATIVE_REPLACE_ARTICLE_MESSAGE_TYPE, false);
          await this.articleOnMountingRailInserted(data);
        } catch (error) {
          Log.error('Could not insert article', error);
          await this.replaceArticleOnMountingRail_failure();
        }
      }
    } else {
      await this.completeReplaceOperation();
    }
  }

  private async completeReplaceOperation(): Promise<void> {
    await this.pushUndoAlternativeReplaceOperation(this.getAdditionalUndoOperations());
    await this.detachArticleContainers(this._articleContainersToDetach);
    this.selectionModelService.selectArticlesByUsageIds(this.selectedArticleIds, true);
    const result = this.insertedArticleUsageIds.length > 0;
    this.clear();

    this._replacedArticles$.next(result);
  }

  private async replaceAccessoriesByAlternative(): Promise<void> {
    if (!this.isActive) {
      return;
    }

    const createdContainers: ArticleContainer[] = this._creationManager.getCreatedArticleContainers();
    if (ArrayUtils.isEmpty(createdContainers)) {
      await this.replaceNextArticle();
      return;
    }

    createdContainers.map((c) => c.articleGUID).forEach((guid) => this._articleIdToReplacedArticleId.setValue(guid, this.processedUsageId));
    this.storeReplacedTextureAreasInfo([this.containerService.findArticleContainerInGlobal(this.processedUsageId)]);

    // for accessories, the move map will always be empty
    await this.processingCreatedArticles(ArticleContainerUtils.extractArticleGUIDs(createdContainers), new Dictionary<string, Vector3>());
    // TODO: (from flex) if instead of one container, multiple containers are created, then we try to reconnect accessory articles
    // from one to multiple containers. Check situation when one accessory can reconect to multiple articles.
    await this.postProcessCreatedContainers(createdContainers);

    const detachContainers: ArticleContainer[] = this.containerService.findArticleContainersInGlobalByGuids(
      this._creationManager.detachArticleUsageIds
    );
    ArrayUtils.mergeArrays(this._articleContainersToDetach, detachContainers);
  }

  private async articleOnMountingRailInserted(data: InsertedArticleData): Promise<void> {
    if (!this.isActive) {
      return;
    }
    data.insertedArticleGUIDs.forEach(guid => this._articleIdToReplacedArticleId.setValue(guid, this._processedArticleContainer.articleGUID));
    this.storeReplacedTextureAreasInfo([this.containerService.findArticleContainerInGlobal(this.processedUsageId)]);
    await this.processingCreatedArticles(data.insertedArticleGUIDs, data.moveArticlesMap);
    const createdContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(data.baseArticleGUID);
    await this.postProcessCreatedContainer(createdContainer, this._processedArticleContainer);
  }

  private async postProcessCreatedContainers(createdContainers: ArticleContainer[]): Promise<void> {
    const commonContainers: ArticleContainer[] = this._commonArticleContainers.concat([this._processedArticleContainer]);
    for (const createdContainer of createdContainers) {
      for(const usageIdentity of ArticleUsageUtils.getForwardConnectedConnectionUsageIds(createdContainer.articleUsage)) {
        const processedContainer: ArticleContainer = this.articleContainerUtils.findArticleContainerByGUID(
          usageIdentity.articleUsageId,
          commonContainers
        );
        if (processedContainer) {
          commonContainers.splice(commonContainers.indexOf(processedContainer), 1);
          await this.postProcessCreatedContainer(createdContainer, processedContainer);
        }
      }
    }

    this._commonArticleContainers = [];
    ArrayUtils.removeAllItemsInArray(this._articleContainersToDetach, commonContainers);
    this.revertArticleDetachOperations(commonContainers);
  }

  private async postProcessCreatedContainer(createdContainer: ArticleContainer, replacedContainer: ArticleContainer): Promise<void> {
    if (!createdContainer || !replacedContainer) {
      return;
    }
    const replacedArticleUsage: ArticleUsageVO = replacedContainer.articleUsage;
    this.updateStrippedCompositionInfo(createdContainer, replacedContainer);

    const replacedArticleGuid: string = replacedContainer.articleGUID;
    const createdUsageCompositeArticleId: number = ArticleUsageUtils.getArticleUsageInfoPropertyIntValue(
      createdContainer.articleUsage,
      ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_ID
    );
    const replacedUsageCompositeArticleId: number = ArticleUsageUtils.getArticleUsageInfoPropertyIntValue(
      replacedArticleUsage,
      ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_ID
    );
    const strippedCompositeArticleUsageId: string = ArticleUsageUtils.getStrippedCompositeArticleUsageIdFromUsage(replacedArticleUsage);
    if (
      StringUtils.isEmpty(strippedCompositeArticleUsageId) &&
      !isNaN(replacedUsageCompositeArticleId) &&
      replacedUsageCompositeArticleId > 0 &&
      (isNaN(createdUsageCompositeArticleId) || createdUsageCompositeArticleId < 1)
    ) {
      CompositeArticleUtils.copyCompositeArticleInformationFromUsage(
        replacedContainer.articleUsage,
        createdContainer.articleUsage,
        ArticleUsageUtils.getArticleUsageInfoPropertyStringValue(replacedArticleUsage, ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_USAGE_ID)
      );
    }
    const acceptingConections: Connection[] = ConnectionInfoUtils.getFilteredAcceptingCubesByArticleContainers([createdContainer]);
    const accessoryArticleContainers: ArticleContainer[] = this.getConnectedArticleContainers(replacedContainer, createdContainer);
    let accessoryAlternativeConnectionDatas: AlternativeUsageConnectionData[];
    let alternativeUsageConnectionDatas: AlternativeUsageConnectionData[] = [];
    let notReconnectedAccessories: ArticleContainer[] = [];

    if (ArticleUsageUtils.isCompositePartArticleUsage(replacedContainer.articleUsage)) {
      const connectedArticleComponents: ArticleContainer[] = this.containerService.getConnectedArticleContainers(replacedContainer.articleUsage);
      for (const compositePartContainer of connectedArticleComponents) {
        // coding fingers are not supposed to be reconnected; this also skips attached articles
        // which are already included in accessory handling
        if (accessoryArticleContainers.indexOf(compositePartContainer) === -1 && !CodingArticleUtils.isCodingFinger(compositePartContainer.article)) {
          accessoryArticleContainers.push(compositePartContainer);
        }
      }
    }
    for (const accessoryContainer of accessoryArticleContainers) {
      accessoryAlternativeConnectionDatas = await this.reconnectAccessoryArticle(accessoryContainer, replacedContainer, acceptingConections);
      if (accessoryAlternativeConnectionDatas.length > 0) {
        this.removeContainersFromArticlesToDetach([accessoryContainer]);
        const childOfAccessoryArticlesToReconnect: ArticleContainer[] = this.insertArticleUtils.findChildArticleContainers([accessoryContainer]);
        this.removeContainersFromArticlesToDetach(childOfAccessoryArticlesToReconnect);
        ArticleUsageUtils.removeAssociationsWithUsage(replacedArticleUsage, accessoryContainer.articleUsage.id);
        ArticleUsageUtils.removeAssociationsWithUsage(accessoryContainer.articleUsage, replacedArticleGuid);
        alternativeUsageConnectionDatas = alternativeUsageConnectionDatas.concat(accessoryAlternativeConnectionDatas);
      } else {
        notReconnectedAccessories.push(accessoryContainer);
      }
    }

    this._articleIdToAlternativeUsageConnectionDatas.setValue(replacedArticleGuid, alternativeUsageConnectionDatas);

    if (
      CodingArticleUtils.hasCodingFingerConnection(createdContainer.article) &&
      CodingArticleUtils.hasCodingFingerConnection(replacedContainer.article)
    ) {
      if (!CodingArticleUtils.hasMultipleCodingFingerConnections(createdContainer.article)) {
        const removedCodingFingerArticleId: String = CodingArticleUtils.getRemovedCodingFingerArticleId(replacedContainer.articleUsage);
        if (removedCodingFingerArticleId != null && removedCodingFingerArticleId.length > 0) {
          await this.detachCodingFingerArticleService.detachCodingFingersFromArticles(this, [createdContainer.articleUsage.id], false);
        }
      } else {
        const usageIdList: string[] = this.getCodingArticleUsageIdsToRemove(replacedContainer, createdContainer);
        if (usageIdList.length > 0) {
          await this.detachCodingFingerArticleService.detachCodingFingersFromArticles(this, usageIdList, false);
        }
      }
    }

    this.copyRemovedContactInformation(replacedContainer, createdContainer);

    if (notReconnectedAccessories.length > 0) {
      const childOfAccessoryArticlesToDelete: ArticleContainer[] = this.insertArticleUtils.findChildArticleContainers(notReconnectedAccessories);
      notReconnectedAccessories = notReconnectedAccessories.concat(childOfAccessoryArticlesToDelete);
      await this.detachArticleContainers(notReconnectedAccessories);
    }

    if (this.commonMoveArticlesMap && this.commonMoveArticlesMap.containsKey(replacedArticleGuid)) {
      this.commonMoveArticlesMap.remove(replacedArticleGuid);
    }

    this.reapplyTextAndImageToConnections(ConnectionInfoUtils.findTextConnections(createdContainer), ConnectionInfoUtils.findTextConnections(replacedContainer));
  }

  protected removeContainersFromArticlesToDetach(articleContainers: ArticleContainer[]): void {
    super.removeContainersFromArticlesToDetach(articleContainers);
    for (const articleContainer of articleContainers) {
      if (this._articleContainersToDetach.indexOf(articleContainer) !== -1) {
        this._articleContainersToDetach.splice(this._articleContainersToDetach.indexOf(articleContainer), 1);
      }
    }
  }

  private getCodingArticleUsageIdsToRemove(replacedContainer: ArticleContainer, createdContainer: ArticleContainer): string[] {
    const usageIdList: string[] = [];

    // collect emptyCoding Finger positions on replaced article
    const emptyCodingFingerPositions: string[] = [];
    let i: number = 0;
    let connectionAreaVO: ConnectionAreaVO;
    while (i <= replacedContainer.article.metaElements.length) {
      connectionAreaVO = replacedContainer.article.metaElements[i] as ConnectionAreaVO;
      if (connectionAreaVO) {
        const typeName: string = PropertyUtils.getStringPropertyValue(connectionAreaVO.connectionRequirements, ArticleConnectionPropertyDef.TYPE);
        if (typeName === ArticleConnectionPropertyValue.CODING_FINGER_CONNECTION_TYPE) {
          if (!ArticleUsageUtils.isConnectionInUse(replacedContainer.articleUsage, connectionAreaVO.name)) {
            const position: string = CodingArticleUtils.getPositionStringFromConnection(connectionAreaVO);
            emptyCodingFingerPositions.push(position);
          }
        }
      }
      i++;
    }
    i = 0;
    // collect usageIds on created article
    while (i <= createdContainer.article.metaElements.length) {
      connectionAreaVO = createdContainer.article.metaElements[i] as ConnectionAreaVO;
      if (connectionAreaVO) {
        const typeName: string = PropertyUtils.getStringPropertyValue(connectionAreaVO.connectionRequirements, ArticleConnectionPropertyDef.TYPE);
        if (typeName === ArticleConnectionPropertyValue.CODING_FINGER_CONNECTION_TYPE) {
          if (ArticleUsageUtils.isConnectionInUse(createdContainer.articleUsage, connectionAreaVO.name)) {
            const position: string = CodingArticleUtils.getPositionStringFromConnection(connectionAreaVO);
            if (emptyCodingFingerPositions.indexOf(position) !== -1) {
              const usageId: string = ArticleUsageUtils.getFirstConnectedArticleUsageIdOrNullByConnectionName(createdContainer.articleUsage, connectionAreaVO.name);
              if (usageId && usageId.length > 0) {
                usageIdList.push(usageId);
              }
            }
          }
        }
      }
      i++;
    }

    return usageIdList;
  }

  private copyRemovedContactInformation(replacedContainer: ArticleContainer, createdContainer: ArticleContainer): void {
    let contactId: string;
    let contactVersion: string;
    const emptyContactPositions: string[] = [];
    const removedContactConnectionNames: string[] = [];
    let i: number = 0;
    let connectionAreaVO: ConnectionAreaVO;
    for (const definedMetaInfoProperty of replacedContainer.articleUsage.definedMetaElementInfoProperties) {
      if (definedMetaInfoProperty.property.name === ArticleUsageInfoPropertyDef.REMOVED_CODING_FINGER_ARTICLE_ID) {
        contactId = ArticleUsageUtils.getDefinedMetaElementInfoPropertyStringValue(
          replacedContainer.articleUsage,
          definedMetaInfoProperty.identity.metaElementName,
          ArticleUsageInfoPropertyDef.REMOVED_CODING_FINGER_ARTICLE_ID
        );
        removedContactConnectionNames.push(definedMetaInfoProperty.identity.metaElementName);
      } else if (definedMetaInfoProperty.property.name === ArticleUsageInfoPropertyDef.REMOVED_CODING_FINGER_ARTICLE_VERSION) {
        contactVersion = ArticleUsageUtils.getDefinedMetaElementInfoPropertyStringValue(
          replacedContainer.articleUsage,
          definedMetaInfoProperty.identity.metaElementName,
          ArticleUsageInfoPropertyDef.REMOVED_CODING_FINGER_ARTICLE_VERSION
        );
      }
    }
    while (i <= replacedContainer.article.metaElements.length) {
      connectionAreaVO = replacedContainer.article.metaElements[i] as ConnectionAreaVO;
      if (connectionAreaVO) {
        const typeName: string = PropertyUtils.getStringPropertyValue(connectionAreaVO.connectionRequirements, ArticleConnectionPropertyDef.TYPE);
        if (typeName === ArticleConnectionPropertyValue.CONTACT_CONNECTION_TYPE) {
          if (removedContactConnectionNames.indexOf(connectionAreaVO.name) !== -1) {
            const position: string = CodingArticleUtils.getPositionStringFromConnection(connectionAreaVO);
            emptyContactPositions.push(position);
          }
        }
      }
      i++;
    }
    i = 0;
    while (i <= createdContainer.article.metaElements.length) {
      connectionAreaVO = createdContainer.article.metaElements[i] as ConnectionAreaVO;
      if (connectionAreaVO != null) {
        const typeName: string = PropertyUtils.getStringPropertyValue(connectionAreaVO.connectionRequirements, ArticleConnectionPropertyDef.TYPE);
        if (typeName === ArticleConnectionPropertyValue.CONTACT_CONNECTION_TYPE) {
          const position: string = CodingArticleUtils.getPositionStringFromConnection(connectionAreaVO);
          if (emptyContactPositions.indexOf(position) !== -1) {
            ArticleUsageUtils.setDefinedMetaElementInfoPropertyStringValue(
              createdContainer.articleUsage,
              connectionAreaVO.name,
              ArticleUsageInfoPropertyDef.REMOVED_CODING_FINGER_ARTICLE_ID,
              contactId
            );
            ArticleUsageUtils.setDefinedMetaElementInfoPropertyStringValue(
              createdContainer.articleUsage,
              connectionAreaVO.name,
              ArticleUsageInfoPropertyDef.REMOVED_CODING_FINGER_ARTICLE_VERSION,
              contactVersion
            );
            ArticleUsageUtils.setDefinedMetaElementInfoPropertyStringValue(
              createdContainer.articleUsage,
              connectionAreaVO.name,
              ArticleUsageInfoPropertyDef.REMOVED_CODING_FINGER_COMPOSITE_PART_USAGE_ID,
              createdContainer.articleUsage.id
            );
          }
        }
      }
      i++;
    }
  }

  private reapplyTextAndImageToConnections(targetTextConnections: Connection[], sourceTextConnections: Connection[]): void {
    if (ArrayUtils.isEmpty(targetTextConnections) || ArrayUtils.isEmpty(sourceTextConnections)) {
      return;
    }

    let invalidate3DViewRequired: boolean = false;
    for (const sourceTextConnection of sourceTextConnections) {
      const nearestTextConnection: Connection = this.getNearestConnectionInSource(sourceTextConnection, targetTextConnections, true);
      if (!nearestTextConnection) {
        continue;
      }

      targetTextConnections.splice(targetTextConnections.indexOf(nearestTextConnection), 1);

      let textApplied: boolean = false;
      if (this.isEqualLabelingConnections(sourceTextConnection, nearestTextConnection)) {
        textApplied = this.applyTextToConnection(sourceTextConnection, nearestTextConnection);
      }

      const imageApplied: boolean = this.applyImageToConnection(sourceTextConnection, nearestTextConnection);
      // textures with image-usage will be refreshed beforehand (in time of applying corresponding image),
      // so only textures with text need to be refreshed here
      if (textApplied && !imageApplied) {
        nearestTextConnection.resetTexture();
        nearestTextConnection.refreshTexture();
        invalidate3DViewRequired = true;
      } else {
        invalidate3DViewRequired = false;
      }

      if (ArrayUtils.isEmpty(targetTextConnections)) {
        break;
      }
    }
    if (invalidate3DViewRequired) {
      this.sceneManager.renderScene();
    }
  }

  private applyTextToConnection(sourceConnection: Connection, targetConnection: Connection): boolean {
    if (!sourceConnection || !sourceConnection.metaElementUsage || !(sourceConnection.metaElementUsage instanceof TextureAreaUsageVO)) {
      return false;
    }

    let textApplied: boolean = false;
    if (!targetConnection.metaElementUsage) {
      LabelingUtils.applyTextureMetaElementUsageIfNotExists(targetConnection, this.projectArticleService, this.projectMetaElementService);
    }
    const targetMetaUsage: MetaElementUsageVO = targetConnection.metaElementUsage;
    if (targetMetaUsage instanceof TextureAreaUsageVO) {
      const targetUsage: TextureAreaUsageVO = targetMetaUsage as TextureAreaUsageVO;
      const sourceTextValue: TextValueVO = (targetConnection.metaElementUsage as TextureAreaUsageVO).textValue;
      if ((sourceTextValue && !sourceTextValue.equals(targetUsage.textValue)) || (!sourceTextValue && targetUsage.textValue)) {
        targetUsage.textValue = sourceTextValue;
        textApplied = true;
      }
    }
    return textApplied;
  }

  private applyImageToConnection(sourceConnection: Connection, targetConnection: Connection): boolean {
    if (!sourceConnection || !sourceConnection.imageBitmap || Connection.TYPE_TEXT_OR_IMAGE_USAGE !== targetConnection.type) {
      return false;
    }

    const customImageHash: string = ArticleUsageUtils.getDefinedMetaElementInfoPropertyStringValue(
      sourceConnection.articleContainer.articleUsage,
      sourceConnection.connectionName,
      DefinedMetaElementInfoPropertyDef.CUSTOM_IMAGE_HASH
    );
    if (!customImageHash) {
      return false;
    }

    ConnectionInfoUtils.updateImageHashForConnections(customImageHash, [targetConnection]);
    this.imageTextureProcessor.applyTextureToConnection(customImageHash, targetConnection);
    return true;
  }

  private updateStrippedCompositionInfo(createdContainer: ArticleContainer, replacedContainer: ArticleContainer): void {
    const replacedUsage: ArticleUsageVO = replacedContainer.articleUsage;
    const strippedCompositeArticleUsageId: string = ArticleUsageUtils.getStrippedCompositeArticleUsageIdFromUsage(replacedUsage);
    if (strippedCompositeArticleUsageId) {
      let articlesToUpdate: ArticleContainer[] = [];
      if (this.compositeArticleInfoUtils.isCompositePartArticle(createdContainer)) {
        articlesToUpdate = this.containerService.getCompositePartArticles(createdContainer);
      } else {
        articlesToUpdate.push(createdContainer);
      }

      // part-usage-id is original id of article-usage specified in project of corresponding complex article
      // so part-usage-id taken from replaced article should not be used here, because
      // 1. replacement (complex) article could be different
      // 2. replacement article could consist of multiple parts,
      //  so it's not correct to apply the same part-usage-id to all parts of replacement article

      // ArticleUsageUtils.getArticleUsageInfoPropertyStringValue(replacedUsage,
      // ArticleUsageInfoPropertyDef.STRIPPED_COMPOSITE_PART_USAGE_ID);
      const compositePartUsageId: string = null;
      for (const article of articlesToUpdate) {
        CompositeArticleUtils.copyStrippedCompositeArticleInformationFromUsage(replacedUsage, article.articleUsage, compositePartUsageId);
      }
    }
  }

  private getConnectedArticleContainers(article: ArticleContainer, newArticle: ArticleContainer): ArticleContainer[] {
    let connectedArticles: ArticleContainer[];
    if (ArticleUsageUtils.isCompositePartArticleUsage(article.articleUsage)) {
      connectedArticles = this.getConnectedArticleContainersToCompositeArticle(article, newArticle);
    } else {
      connectedArticles = this.containerService.getConnectedArticleContainers(article.articleUsage);
    }
    return connectedArticles;
  }

  private getConnectedArticleContainersToCompositeArticle(article: ArticleContainer, newArticle: ArticleContainer): ArticleContainer[] {
    const result: ArticleContainer[] = [];
    const compositePartArticles: ArticleContainer[] = this.containerService.getCompositePartArticles(article);
    for (const articleContainer of compositePartArticles) {
      const connectedArticlesToPartComposition: ArticleContainer[] = this.containerService.getConnectedArticleContainers(articleContainer.articleUsage);
      for (const connectedArticle of connectedArticlesToPartComposition) {
        if (compositePartArticles.indexOf(connectedArticle) === -1 && connectedArticle.articleUsage.id !== newArticle.articleUsage.id) {
          result.push(connectedArticle);
        }
      }
    }
    return result;
  }

  protected mergeMoveArticlesMap(sourceMoveMap: Dictionary<string, Vector3>, articleGUID: string = null): void {
    super.mergeMoveArticlesMap(sourceMoveMap, null);
  }

  private replaceAccessoriesByAlternative_failure(): void {
    if (!this.isActive) {
      return;
    }
    this.clear();
    this.selectionModelService.selectArticlesByUsageIds(this.selectedArticleIds, true);
    this._replacedArticles$.next(false);
  }

  private async replaceArticleOnMountingRail_failure(): Promise<void> {
    if (!this.isActive) {
      return;
    }
    this._articleContainersToDetach.splice(this._articleContainersToDetach.indexOf(this._processedArticleContainer), 1);
    await this.finalizeFailureProcessingArticle();
    await this.replaceNextWithDelay();
  }

  protected clear(): void {
    super.clear();
    this._commonArticleContainers = [];
  }
}
